import { motion } from 'framer-motion';
import { CheckCircle } from 'lucide-react';
import SEO from '../../components/SEO';
import heroImage from '../../assets/interior-hero.png';

const Interior = () => {
    const features = [
        "Premium Emulsion Finishes",
        "Dust-Free Painting Experience",
        "Furniture & Floor Protection",
        "Color Consultation",
        "Wall Putty & Surface Leveling",
        "Eco-friendly & Low VOC Paints"
    ];

    const fadeInUp = {
        initial: { opacity: 0, y: 30 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true },
        transition: { duration: 0.6 }
    };

    return (
        <div className="min-h-screen pb-20">
            <SEO 
                title="Interior Painting Services Madurai" 
                description="Elevate your home's interior with SSS Home Painting. Best interior painters in Madurai providing premium finishes." 
            />
            
            {/* Hero Section */}
            <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <img src={heroImage} alt="Interior Painting" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-blue-900/70"></div>
                </div>
                <div className="relative z-10 text-center px-4">
                    <motion.h1 
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-4xl md:text-6xl font-bold text-white mb-4"
                    >
                        Interior Painting Services
                    </motion.h1>
                    <motion.p 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                        className="text-xl text-blue-100 italic"
                    >
                        "Transforming your living spaces into masterpieces."
                    </motion.p>
                </div>
            </section>

            <div className="max-w-4xl mx-auto px-4 mt-20">
                <motion.div {...fadeInUp} className="bg-white p-8 md:p-12 rounded-[2rem] shadow-xl border border-slate-100 mb-16">
                    <h2 className="text-3xl font-bold text-blue-900 mb-6 font-display">Professional Interior Solutions</h2>
                    <p className="text-gray-600 mb-8 leading-relaxed text-lg">
                        At SSS Home Painting, we understand that your home's interior is a reflection of your personality. Our expert painters in Madurai provide a seamless, hassle-free painting experience using high-quality materials and modern techniques.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {features.map((feature, i) => (
                            <motion.div 
                                key={i} 
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ delay: i * 0.1 }}
                                className="flex items-center space-x-3 p-4 bg-slate-50 border border-slate-100 rounded-2xl group hover:bg-orange-600 transition-colors duration-300"
                            >
                                <CheckCircle className="text-orange-600 group-hover:text-white transition-colors" />
                                <span className="font-bold text-gray-800 group-hover:text-white transition-colors">{feature}</span>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                <motion.div {...fadeInUp} className="bg-orange-50 p-10 rounded-[2.5rem] border border-orange-100 shadow-inner">
                    <h3 className="text-2xl font-bold text-blue-900 mb-8 flex items-center space-x-3">
                        <span className="bg-blue-900 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">!</span>
                        <span>Our Premium Process</span>
                    </h3>
                    <div className="space-y-6">
                        {[
                            { title: "Site Inspection", desc: "Professional check of wall conditions and moisture levels." },
                            { title: "Surface Preparation", desc: "Expert scraping, cleaning, and premium putty application." },
                            { title: "Priming", desc: "Applying a solid specialized base for the paint." },
                            { title: "Painting", desc: "Two coats of premium emulsion for a rich, luxury finish." },
                            { title: "Cleanup", desc: "Post-painting deep clean to leave your home sparkling." }
                        ].map((step, i) => (
                            <div key={i} className="flex space-x-4">
                                <span className="font-bold text-orange-600 text-lg">{i + 1}.</span>
                                <div>
                                    <h4 className="font-bold text-blue-900">{step.title}</h4>
                                    <p className="text-gray-600">{step.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default Interior;
